/**
 * The general warm-up performed before every workout.
 *
 * Transcribed from the "WARM UP PROTOCOL" section of the source spreadsheets,
 * where it is byte-identical across PPL, Full Body and Upper/Lower. It is the
 * same every session and carries no load, so it is held here on the client
 * rather than in the program data or the database: there is nothing to compare
 * against a previous session and no personal record to track.
 */
export interface PreWorkoutDrill {
  name: string;
  /**
   * The name to look the demo video up under, when it differs from the one
   * shown. The spreadsheet labels this drill "(optional)"; that qualifier is a
   * flag here, so the lookup needs the original wording.
   */
  videoName?: string;
  /** The prescription, shown as-is. Never a number to step. */
  prescription: string;
  optional?: boolean;
  /**
   * Cardio is timed against a window: it counts down to the point where you
   * may stop (`readyAtSecs`), then through to the full duration. Drills
   * without a window are simply stopwatched.
   */
  readyAtSecs?: number;
  fullDurationSecs?: number;
}

export const PRE_WORKOUT_NOTE =
  'Perform this general warm-up before every workout — it should take 5-10 minutes '
  + 'at most. You can save time by doing some of the dynamic stretches while you do '
  + 'the warm-up sets for your first exercise.';

export const PRE_WORKOUT_DRILLS: PreWorkoutDrill[] = [
  {
    name: 'Light cardio on a machine of your choice (treadmill, stairmaster, elliptical, bike, etc.)',
    prescription: '5-10 minutes',
    // Green at 5 minutes: from there on you can stop whenever you like.
    readyAtSecs: 5 * 60,
    fullDurationSecs: 10 * 60,
  },
  { name: 'Arm Swings', prescription: '10 reps per side' },
  { name: 'Arm Circles', prescription: '10 reps per side' },
  { name: 'Front-to-Back Leg Swings', prescription: '10 reps per side' },
  { name: 'Side-to-Side Leg Swings', prescription: '10 reps per side' },
  {
    name: 'Cable External Rotation',
    videoName: 'Cable External Rotation (optional)',
    prescription: '15 reps per side',
    optional: true,
  },
];

/** True when this drill is timed against a window rather than stopwatched. */
export const isTimed = (d: PreWorkoutDrill): boolean =>
  d.readyAtSecs != null && d.fullDurationSecs != null;

// ── Completion state ────────────────────────────────────────────────────────
// Never sent to the API — there is nothing here worth recording. localStorage
// rather than sessionStorage so ticks survive the browser being closed and
// reopened mid-workout, matching how the timers behave.

const key = (sessionId: string) => `gymcoach_prewarmup_${sessionId}`;

export function loadCompleted(sessionId: string): number[] {
  try {
    const raw = localStorage.getItem(key(sessionId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(n => typeof n === 'number') : [];
  } catch {
    return [];
  }
}

export function saveCompleted(sessionId: string, indices: number[]): void {
  try {
    localStorage.setItem(key(sessionId), JSON.stringify(indices));
  } catch {
    // Private-mode storage failures are not worth interrupting a workout for.
  }
}

export function clearCompleted(sessionId: string): void {
  try {
    localStorage.removeItem(key(sessionId));
  } catch { /* ignore */ }
}
